import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { signup as apiSignup } from '../api';
import { useAuth } from '../context/AuthContext';

type Plan = 'free' | 'plus' | 'pro';

export default function SignupPage() {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [city, setCity] = useState('');
  const [plan, setPlan] = useState<Plan>('free');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState<{ cityBlocked: boolean } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await apiSignup({ email, password, name, company_name: companyName, city, plan });
      login(result.token, result.user);
      setSuccess({ cityBlocked: result.cityBlocked });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Oprettelse fejlede');
    } finally {
      setLoading(false);
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="w-full max-w-md bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="mx-auto w-12 h-12 bg-[var(--color-success-100)] rounded-full flex items-center justify-center mb-4">
            <svg className="w-6 h-6 text-[var(--color-success)]" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/></svg>
          </div>
          <h1 className="text-xl font-bold text-gray-900 mb-2">Konto oprettet</h1>
          <p className="text-sm text-gray-600 mb-4">
            Vi har sendt en bekræftelses-mail til <strong>{email}</strong>. Klik på linket i mailen for at aktivere din konto.
          </p>
          {success.cityBlocked && (
            <div className="bg-[var(--color-warning-100)] border border-yellow-200 rounded-lg p-3 mb-4 text-sm text-yellow-800 text-left">
              Der er allerede flere udlejere i <strong>{city}</strong>. Din konto skal manuelt godkendes — du får besked når den er klar.
            </div>
          )}
          <Link to="/" className="text-sm text-[var(--color-primary)] hover:underline">Gå til log ind</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4 py-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold tracking-tight" style={{ letterSpacing: '-0.025em' }}>
            <span style={{ color: '#131916' }}>RentalFleet</span>
            <span className="text-sage-700">OS</span>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Opret din konto</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dit navn</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
              required autoFocus />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
              required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Adgangskode</label>
            <input type="password" value={password} onChange={e => setPassword(e.target.value)}
              minLength={8}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
              placeholder="Mindst 8 tegn"
              required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Firmanavn</label>
            <input type="text" value={companyName} onChange={e => setCompanyName(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
              required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">By hvor du udlejer</label>
            <input type="text" value={city} onChange={e => setCity(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]"
              placeholder="fx Aalborg"
              required />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Pakke</label>
            <select value={plan} onChange={e => setPlan(e.target.value as Plan)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:outline-none focus:ring-[3px] focus:ring-[var(--color-border-focus)]/15 focus:border-[var(--color-border-focus)]">
              <option value="free">Gratis</option>
              <option value="plus">Plus</option>
              <option value="pro">Pro</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Plus/Pro afventer manuel aktivering indtil betaling er sat op.</p>
          </div>

          {error && (
            <p className="text-sm text-[var(--color-error)] bg-[var(--color-error-100)] border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}

          <button type="submit" disabled={loading}
            className="w-full bg-[var(--color-primary)] hover:bg-[var(--color-primary-hover)] disabled:opacity-50 text-white font-medium rounded-lg px-4 py-2 text-sm transition-colors">
            {loading ? 'Opretter…' : 'Opret konto'}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Har du allerede en konto? <Link to="/" className="text-[var(--color-primary)] hover:underline">Log ind</Link>
        </p>
      </div>
    </div>
  );
}
